import { useEffect, useRef, useState } from 'react'

import { Box, keyframes } from '@mui/material'
import { useTheme } from '@mui/material/styles'

import { brandTintText, navSelected } from '@repo/ui'

/**
 * The setup-progress pill — "1/6" (VRG-340).
 *
 * ONE component because the same count appears in two places that must not drift: the sidebar
 * Home row, where it is the only always-visible record of progress, and the checklist card's
 * own header. Those are far enough apart on screen that a reader only recognises them as the
 * same fact if they look identical, and near enough in purpose that two sets of inline styles
 * would be the second hand-roll §36 exists to stop.
 *
 * Brand purple, matching the ticks and the progress bar, because all three report the same
 * thing. It is deliberately NOT the `success` green: a completed setup step is not a success
 * in the alert sense — nothing went right or wrong — it is progress through the product, so it
 * reads in the product's own colour.
 */
/**
 * A single soft swell when the count changes.
 *
 * Deliberately a SCALE + shadow ripple rather than a colour flash: the pill is already brand
 * purple, so a colour change would have to leave the brand to be visible, and a flash reads as
 * an alert. This reads as "something landed here".
 */
const swell = keyframes`
  0%   { transform: scale(1);    box-shadow: 0 0 0 0 currentColor; }
  35%  { transform: scale(1.18); box-shadow: 0 0 0 6px transparent; }
  100% { transform: scale(1);    box-shadow: 0 0 0 0 transparent; }
`

export const ProgressPill = ({ children }: { children: React.ReactNode }) => {
  const theme = useTheme()

  /*
    PULSE ON CHANGE — the other half of the progress toast.
    
    Every checklist step is completed on some page that is not Home, so the toast reports it
    from the corner while the number that actually changed sits in the nav. The toast says WHAT
    happened; this says WHERE it lives. Together they close the loop without a second overlay
    pattern anchored to the sidebar — an anchor that would attach to a 24px icon whose geometry
    moves the moment the collapsed sidebar expands on hover.

    Skips the FIRST value: mounting is not a change, and pulsing on every page load would make
    the badge look like it is nagging rather than reporting.
  */
  const previous = useRef<React.ReactNode>(null)
  const [pulsing, setPulsing] = useState(false)

  useEffect(() => {
    const isFirst = previous.current === null
    const changed = !isFirst && previous.current !== children
    previous.current = children
    if (!changed) return
    setPulsing(true)
    const t = window.setTimeout(() => setPulsing(false), 600)
    return () => window.clearTimeout(t)
  }, [children])

  return (
    <Box
      component="span"
      sx={{
        flexShrink: 0,
        px: 2,
        py: 0.5,
        borderRadius: 999,
        fontSize: 12,
        lineHeight: 1.5,
        whiteSpace: 'nowrap',
        // `navSelected` — brand[500] at a mode-aware alpha — is THE Vera purple tint in this
        // app: the selected sidebar row and every MenuButton already use it. Reusing it means
        // the pill is the same purple as the nav rather than a fourth one.
        //
        // It replaced `brand[100]`, which is a LIGHT-MODE tint (#EEEBFC): in dark mode that
        // rendered as a near-white lozenge. A fixed step off the brand ramp cannot serve both
        // themes; an alpha over the surface can, which is why the helper takes the theme.
        bgcolor: navSelected(theme),
        // The 700/300 pair this pill derived is now shared (`brandTintText`), because the Tour
        // chip wanted the same foreground-on-brand-tint and would otherwise have been a second
        // copy of the same ternary — §36. Same measured values, one home.
        color: brandTintText(theme),
        ...(pulsing && { animation: `${swell} 600ms ease-out` }),
        // Honour a reduced-motion preference — this is decorative reinforcement, and the
        // number itself already carries the information.
        '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
      }}
    >
      {children}
    </Box>
  )
}
